import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import noop from 'lodash/noop';
import {
  useFacilityMenuGet,
  useFacilityMenuItems,
} from 'shared-feature-facility-menu';

import {
  useAccountGet,
  useApi,
  useWebPunchSession,
} from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  SectionTabs,
  SetupNav,
  TopNav,
  useBreadcrumbLabelsBuilder,
} from '@sbv-clients/shared-feature-layouts';
import { useMobileDrawerStore } from '@sbv-clients/shared-state';
import {
  Box,
  ButtonBase,
  CssBaseline,
  GlobalStyles,
  styled,
  usePreferenceContext,
  useTheme,
} from '@sbv-clients/ui-shared';
import { addDays, formatDate } from '@sbv-clients/util-misc';

import { useMixpanelIdentity } from '../tracking/use-mixpanel-identity';
import NoAccessState from './no-access-state';

const Main = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: theme.custom?.shellPageBg,
}));

// Phones: opens the employee list drawer (the list itself is hidden there).
const MobileEmployeesLink = () => {
  const theme = useTheme();
  const { setMobileDrawerOpen } = useMobileDrawerStore();
  return (
    <ButtonBase
      onClick={() => setMobileDrawerOpen(true)}
      sx={{
        display: { xs: 'flex', sm: 'none' },
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: '4px',
        padding: '10px 12px 0',
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 500,
        color: theme.custom?.brandIndigo,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        aria-hidden
        sx={{
          width: 16,
          height: 16,
          flexShrink: 0,
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      >
        <path d="m14 6-6 6 6 6" />
      </Box>
      Employees
    </ButtonBase>
  );
};

const PrivateRouteLayout = () => {
  const { showSideBar, showNavBar } = usePreferenceContext();
  const { pathname } = useLocation();
  const { data: facilityMenuItems = [] } = useFacilityMenuGet();
  const { breadCrumbLabels } = useBreadcrumbLabelsBuilder(facilityMenuItems);
  const { data: accountData } = useAccountGet();
  const {
    allMenuItems,
    isSuccess: isMenuLoaded,
    parentMenuWithSubMenuItemsType,
  } = useFacilityMenuItems();
  const hasNoTabs = isMenuLoaded && !allMenuItems?.length;

  useMixpanelIdentity();

  const api = useApi();
  const { isMobileSession } = useWebPunchSession();
  const isSupportSession = accountData?.isSupportSession ?? false;
  useEffect(() => {
    //we are caching the census so that's why we are making blind api call
    if (isMobileSession || isSupportSession) {
      return;
    }
    api.complianceApi
      .complianceCensusGet({
        startDate: formatDate(addDays(new Date(), -30)),
        endDate: formatDate(new Date()),
      })
      .catch(noop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileSession, isSupportSession]);

  const menuItems = useMemo(
    () => parentMenuWithSubMenuItemsType ?? [],
    [parentMenuWithSubMenuItemsType]
  );

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
      <CssBaseline />
      <GlobalStyles />
      {(showNavBar || showSideBar) && (
        <TopNav
          items={menuItems}
          showMenu={showSideBar}
          breadCrumbLabels={breadCrumbLabels}
          showContactUs={showNavBar}
          showRemoteSupport={showNavBar && !isMobileSession}
          showPreferences={showNavBar && !isMobileSession}
          showProfiles={showNavBar}
          showResetConfiguration={showNavBar && !isMobileSession}
          profileImage={accountData?.profileImage ?? ''}
        />
      )}
      {showSideBar && <SectionTabs items={menuItems} />}
      <Main>
        {pathname?.includes('/employees') && <MobileEmployeesLink />}
        <Box flex={1} minHeight={0} overflow={{ xs: 'auto', sm: 'hidden' }}>
          <SetupNav items={menuItems}>
            {hasNoTabs ? <NoAccessState /> : <Outlet />}
          </SetupNav>
        </Box>
      </Main>
    </Box>
  );
};

export default PrivateRouteLayout;
